import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../lib/api";

const kgFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
const pctFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });

// One hue per stage, darkest at the raw end. Every bar is also labelled, so
// the chart never relies on colour alone.
const C_HEADON = "#1e3a8a";
const C_HL = "#2563eb";
const C_FINISHED = "#60a5fa";

function csvEscape(value) {
  if (value === null || value === undefined) return "";
  const s = String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function StatTile({ label, value, sub }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold text-slate-900">{value}</div>
      {sub ? <div className="mt-1 text-xs text-slate-500">{sub}</div> : null}
    </div>
  );
}

function StageBar({ label, value, max, colour }) {
  const width = max > 0 ? Math.max((value / max) * 100, 0.5) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="w-40 shrink-0 text-xs text-slate-600">{label}</div>
      <div className="h-3 flex-1 rounded-full bg-slate-100">
        <div
          className="h-3 rounded-full"
          style={{ width: `${width}%`, backgroundColor: colour }}
        />
      </div>
      <div className="w-28 shrink-0 text-right text-xs font-medium text-slate-800">
        {kgFmt.format(value)} kg
      </div>
    </div>
  );
}

export default function HeadOnVsFinishedPlantDetail() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(true);
  const [error, setError] = useState("");
  const [detailError, setDetailError] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [plant, setPlant] = useState("");
  const [search, setSearch] = useState("");
  // Filter changes fire overlapping requests. Without these guards an earlier
  // request that resolves late overwrites the newer answer, and the page shows
  // figures for a filter the user has already changed.
  const summarySeq = useRef(0);
  const detailSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++summarySeq.current;
    setLoading(true);
    setError("");
    const body = { from_date: fromDate, to_date: toDate, plant };
    try {
      const s = await api.post("/ai/ceo-chat/headon-vs-finished-plant", body);
      if (seq !== summarySeq.current) return; // superseded
      setSummary(s.data);
    } catch (err) {
      if (seq !== summarySeq.current) return;
      setError(formatErr(err));
    } finally {
      if (seq === summarySeq.current) setLoading(false);
    }
  }, [fromDate, toDate, plant]);

  const loadDetail = useCallback(async () => {
    const seq = ++detailSeq.current;
    setDetailLoading(true);
    setDetailError("");
    const body = { from_date: fromDate, to_date: toDate, plant, limit: 2000 };
    try {
      const d = await api.post(
        "/ai/ceo-chat/headon-vs-finished-plant/detail",
        body
      );
      if (seq !== detailSeq.current) return; // superseded
      setDetail(d.data);
    } catch (err) {
      if (seq !== detailSeq.current) return;
      setDetailError(formatErr(err));
    } finally {
      if (seq === detailSeq.current) setDetailLoading(false);
    }
  }, [fromDate, toDate, plant]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const rows = useMemo(() => summary?.rows || [], [summary]);
  const totals = summary?.totals || {};
  const maxKg = useMemo(
    () => Math.max(0, ...rows.map((r) => r.head_on_kg || 0)),
    [rows]
  );

  const notComparable = rows.filter((r) => !r.comparable);

  const filteredDetail = useMemo(() => {
    const list = detail?.rows || [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((r) =>
      [r.plant, r.lot_numbers, r.grading_date, r.freezing_plants]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [detail, search]);

  const exportCsv = useCallback(() => {
    const head = [
      "Plant",
      "Grading Date",
      "Lot Numbers",
      "Lots",
      "Head-On (kg)",
      "Headless (kg)",
      "Finished (kg)",
      "Head-On to Finished %",
      "Frozen At",
    ];
    const lines = [head.join(",")].concat(
      filteredDetail.map((r) =>
        [
          r.plant,
          r.grading_date,
          r.lot_numbers,
          r.lots,
          r.head_on_kg,
          r.hl_kg,
          r.finished_kg,
          r.ho_to_finished_pct ?? "",
          r.freezing_plants,
        ]
          .map(csvEscape)
          .join(",")
      )
    );
    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "head-on-vs-finished-by-plant.csv";
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredDetail]);

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto max-w-[1500px] space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="max-w-3xl">
            <h1 className="text-xl font-semibold text-slate-900">
              Head-On vs Finished Product — by Plant
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              How much whole (head-on) raw material each plant took in, and how
              much finished frozen product came out of that same material. The
              material is followed from the lot through grading to freezing, so
              both figures describe the same shrimp rather than two unrelated
              period totals.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/ceo-command-center?tab=production")}
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Back to Production
          </button>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              From Date
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="mt-1 block rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800"
              />
            </label>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              To Date
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="mt-1 block rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800"
              />
            </label>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Plant
              <select
                value={plant}
                onChange={(e) => setPlant(e.target.value)}
                className="mt-1 block rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800"
              >
                <option value="">All Plants</option>
                {(summary?.plant_options || []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={() => {
                setFromDate("");
                setToDate("");
                setPlant("");
              }}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Reset Filters
            </button>
          </div>
          <div className="mt-2 text-xs text-slate-500">
            {summary?.date_basis
              ? `Date basis: ${summary.date_basis}.`
              : "Date basis: grading date."}{" "}
            {!fromDate && !toDate
              ? "No date filter applied — the full history is shown."
              : ""}
          </div>
        </div>

        {error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
            Loading…
          </div>
        ) : null}

        {!loading && summary ? (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <StatTile
                label="Head-On Received"
                value={`${kgFmt.format(totals.head_on_kg || 0)} kg`}
                sub="Whole shrimp entering processing"
              />
              <StatTile
                label="After Deheading"
                value={`${kgFmt.format(totals.hl_kg || 0)} kg`}
                sub="Headless weight at grading"
              />
              <StatTile
                label="Finished Product"
                value={`${kgFmt.format(totals.finished_kg || 0)} kg`}
                sub="Frozen and packed output"
              />
              <StatTile
                label="Head-On to Headless"
                value={
                  totals.ho_to_hl_pct != null
                    ? `${pctFmt.format(totals.ho_to_hl_pct)}%`
                    : "—"
                }
                sub="Deheading recovery across all plants"
              />
              <StatTile
                label="Plants Compared"
                value={kgFmt.format(totals.plants || 0)}
                sub={
                  totals.plants_not_comparable
                    ? `${totals.plants_not_comparable} shown without a conversion %`
                    : "All plants comparable"
                }
              />
            </div>

            {notComparable.length ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                <div className="text-sm font-semibold text-amber-900">
                  Some plants are shown without a conversion percentage
                </div>
                <ul className="mt-2 space-y-1 text-sm text-amber-800">
                  {notComparable.map((r) => (
                    <li key={r.plant_id}>
                      <span className="font-medium">{r.plant}</span> —{" "}
                      {r.not_comparable_reason} Its intake and deheading figures
                      are still shown and are correct.
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-sm font-semibold text-slate-900">
                Stage-by-stage comparison
              </div>
              <div className="mt-1 text-xs text-slate-500">
                Each plant shows the same material at three stages.
              </div>
              <div className="mt-4 space-y-5">
                {rows.map((r) => (
                  <div
                    key={r.plant_id}
                    className="rounded-xl border border-slate-100 p-4"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <div className="text-sm font-semibold text-slate-900">
                        {r.plant}
                      </div>
                      <div className="text-xs text-slate-600">
                        {r.comparable && r.ho_to_finished_pct != null ? (
                          <>
                            Head-on to finished:{" "}
                            <span className="text-sm font-semibold text-slate-900">
                              {pctFmt.format(r.ho_to_finished_pct)}%
                            </span>
                          </>
                        ) : (
                          <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-800">
                            Finished elsewhere — no conversion shown
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 space-y-2">
                      <StageBar
                        label="Head-on received"
                        value={r.head_on_kg}
                        max={maxKg}
                        colour={C_HEADON}
                      />
                      <StageBar
                        label="After deheading"
                        value={r.hl_kg}
                        max={maxKg}
                        colour={C_HL}
                      />
                      <StageBar
                        label="Finished product"
                        value={r.finished_kg}
                        max={maxKg}
                        colour={C_FINISHED}
                      />
                    </div>
                    <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
                      <span>Lots: {kgFmt.format(r.lots)}</span>
                      <span>
                        Deheading recovery:{" "}
                        {r.ho_to_hl_pct != null
                          ? `${pctFmt.format(r.ho_to_hl_pct)}%`
                          : "—"}
                      </span>
                      {r.finished_other_plant_kg > 0 ? (
                        <span>
                          Frozen at another plant:{" "}
                          {kgFmt.format(r.finished_other_plant_kg)} kg
                        </span>
                      ) : null}
                      {r.unaccounted_kg > 0 ? (
                        <span>
                          Still in progress or transferred out:{" "}
                          {kgFmt.format(r.unaccounted_kg)} kg
                        </span>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    Batch-level records
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    {detailLoading
                      ? "Loading…"
                      : detail?.truncated
                      ? `Showing ${kgFmt.format(
                          filteredDetail.length
                        )} of ${kgFmt.format(
                          detail.matching_batches || 0
                        )} batches — narrow the filters to see them all`
                        : `${kgFmt.format(filteredDetail.length)} batch(es)`}
                  </div>
                </div>
                <div className="flex gap-2">
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search plant, lot, date"
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    onClick={exportCsv}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Export CSV
                  </button>
                </div>
              </div>

              {detailLoading ? (
                <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 p-6 text-center text-sm text-slate-500">
                  Loading batch records…
                </div>
              ) : null}

              {detailError ? (
                <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
                  The batch records could not be loaded: {detailError} The plant
                  figures above are unaffected.
                </div>
              ) : null}

              <div className={`mt-3 overflow-x-auto${detailLoading || detailError ? " hidden" : ""}`}>
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-3">Plant</th>
                      <th className="py-2 pr-3">Grading Date</th>
                      <th className="py-2 pr-3">Lot Numbers</th>
                      <th className="py-2 pr-3 text-right">Head-On</th>
                      <th className="py-2 pr-3 text-right">Headless</th>
                      <th className="py-2 pr-3 text-right">Finished</th>
                      <th className="py-2 pr-3 text-right">To Finished</th>
                      <th className="py-2 pr-3">Frozen At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDetail.slice(0, 300).map((r, i) => (
                      <tr
                        key={`${r.batch_ref}-${i}`}
                        className="border-b border-slate-100"
                      >
                        <td className="py-2 pr-3 text-slate-800">{r.plant}</td>
                        <td className="py-2 pr-3 text-slate-600">
                          {r.grading_date}
                        </td>
                        <td className="py-2 pr-3 text-slate-600">
                          {r.lot_numbers}
                        </td>
                        <td className="py-2 pr-3 text-right text-slate-800">
                          {kgFmt.format(r.head_on_kg)}
                        </td>
                        <td className="py-2 pr-3 text-right text-slate-800">
                          {kgFmt.format(r.hl_kg)}
                        </td>
                        <td className="py-2 pr-3 text-right text-slate-800">
                          {kgFmt.format(r.finished_kg)}
                        </td>
                        <td className="py-2 pr-3 text-right text-slate-800">
                          {r.ho_to_finished_pct != null
                            ? `${pctFmt.format(r.ho_to_finished_pct)}%`
                            : "—"}
                        </td>
                        <td className="py-2 pr-3 text-slate-600">
                          {r.freezing_plants || "Not yet frozen"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredDetail.length > 300 ? (
                  <div className="mt-2 text-xs text-slate-500">
                    Showing the first 300 rows on screen. Export CSV includes all{" "}
                    {kgFmt.format(filteredDetail.length)}.
                  </div>
                ) : null}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 text-xs text-slate-500 shadow-sm">
              <div className="font-semibold text-slate-700">
                How this is calculated
              </div>
              <div className="mt-1">
                Head-on weight is the purchased lot weight, headless weight is
                the graded weight, and finished weight is production plus
                rejection weight at freezing — the same three quantities and
                yield formulas used by the ERP Head On To Finished report.
              </div>
              <div className="mt-2">
                Totals reconcile:{" "}
                {summary?.reconciliation?.ok ? "yes" : "not verified"}. Grain:{" "}
                {summary?.grain || "one row per plant"}.
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
